// src/components/ClaimDetail/ClaimContact.jsx
//
// Accesos rápidos (llamar, Google Voice, mapa, correo) y resumen plegable de los
// datos clave del reclamo. Viven en la cabecera de la ficha, junto al nombre del
// cliente (ClaimHeader), en vez de dentro de la pestaña "Datos".
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { logClaimCallNote } from '../../services/claimNoteService';
import { googleVoiceCallLink } from '../../utils/claimHelpers';
import { formatDateToMMDDYYYY } from '../../utils/dateHelpers';
import { assignedAdjustersOf } from '../../utils/claimAccess';

const lossAddressOf = cr => [cr.lossAddressStreet, cr.lossAddressCity, [cr.lossAddressState, cr.lossAddressZip].filter(Boolean).join(' ')]
  .filter(Boolean).join(', ') || cr.clientAddress || '';

// defaultOpen: true/false fuerza el estado inicial; sin él, abierto en escritorio
// y plegado en móvil.
export const ClaimFacts = ({ record, language, defaultOpen }) => {
  const es = language === 'es';
  const phone = record.clientPhone || record.clientMobile || '';
  const assigned = assignedAdjustersOf(record);
  const facts = [
    ['Claim #', record.claimNumber],
    [es ? 'Empresa' : 'Company', record.companyName],
    [es ? 'Aseguradora' : 'Insurance company', record.insuranceCompanyName],
    [es ? 'Póliza' : 'Policy', record.policyNumber],
    [es ? 'Fecha de pérdida' : 'Date of loss', record.incidentDate ? formatDateToMMDDYYYY(record.incidentDate) : ''],
    [es ? 'Causa' : 'Cause', record.cause],
    ['P.A Assigned Adjuster', assigned.join(', ')],
    [es ? 'Teléfono' : 'Phone', phone],
    [es ? 'Correo' : 'Email', record.clientEmail],
    [es ? 'Propiedad' : 'Property', lossAddressOf(record)]
  ];
  // Other Adjuster Access y Attorney solo se muestran si hay alguien.
  const others = (record.accessNames || []).filter(name => !assigned.includes(name));
  if (others.length > 0) facts.push(['Other Adjuster Access', others.join(', ')]);
  if (record.attorneyName) facts.push([es ? 'Abogado' : 'Attorney', record.attorneyName]);
  const missingCount = facts.filter(([, value]) => !value).length;
  // Se decide una vez al montar (no en cada render).
  const [initialOpen] = useState(() => (typeof defaultOpen === 'boolean'
    ? defaultOpen
    : typeof window !== 'undefined' && !!window.matchMedia?.('(min-width: 900px)').matches));
  const copy = (value, label) => navigator.clipboard?.writeText(value)
    .then(() => toast.success(es ? `${label} copiado.` : `${label} copied.`))
    .catch(() => toast.error(es ? 'No se pudo copiar.' : 'Could not copy.'));
  const copyable = [es ? 'Teléfono' : 'Phone', es ? 'Correo' : 'Email'];
  return (
    <details className="claim-facts-details" open={initialOpen}>
      <summary>
        <span>{es ? 'Resumen de datos del reclamo' : 'Claim details summary'}</span>
        {missingCount > 0 && <span className="claim-facts-missing">{es ? `${missingCount} por completar` : `${missingCount} missing`}</span>}
      </summary>
      <dl className="claim-facts">
        {facts.map(([label, value]) => (
          <div key={label} className="claim-fact">
            <dt>{label}</dt>
            <dd className={value ? '' : 'is-missing'}>
              <span className="claim-fact-value">{value || (es ? 'Por completar' : 'Missing')}</span>
              {value && copyable.includes(label) && (
                <button type="button" className="claim-copy" onClick={() => copy(value, label)} aria-label={es ? `Copiar ${label}` : `Copy ${label}`} title={es ? `Copiar ${label}` : `Copy ${label}`}>
                  <i className="fa-regular fa-copy" aria-hidden="true"></i>
                </button>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
};

export const ClaimQuickActions = ({ record, language }) => {
  const es = language === 'es';
  const phone = record.clientPhone || record.clientMobile || '';
  const address = lossAddressOf(record);
  const voice = phone ? googleVoiceCallLink(phone) : null;
  // La llamada queda registrada en las notas del reclamo; no bloquea la llamada
  // ni falla en voz alta si Firestore no responde.
  const logCall = method => {
    if (!record.id) return undefined;
    return logClaimCallNote(record.id, method, { claimNumber: record.claimNumber, clientName: record.clientName })
      .then(() => toast.success(es ? 'Llamada registrada en las notas.' : 'Call logged in notes.'))
      .catch(error => {
        // No bloquea la llamada, pero si la bitácora falla se avisa: antes se perdía en silencio.
        console.error('logClaimCallNote failed:', error);
        toast.error(es ? 'La llamada no quedó registrada en las notas.' : 'The call was not logged in the notes.');
      });
  };
  const missing = text => <span className="claim-action is-disabled" aria-disabled="true" title={text}>{text}</span>;
  return (
    <div className="claim-actions" role="group" aria-label={es ? 'Accesos rápidos' : 'Quick actions'}>
      {phone
        ? <a className="claim-action" href={`tel:${phone.replace(/[^\d+]/g, '')}`} onClick={() => logCall('phone')}><i className="fa-solid fa-phone" aria-hidden="true"></i> {es ? 'Llamar al cliente' : 'Call client'}</a>
        : missing(es ? 'Sin teléfono' : 'No phone')}
      {voice && (
        <a className="claim-action" href={voice} target="_blank" rel="noopener noreferrer" onClick={() => logCall('google_voice')}
          title={es ? 'Abre voice.google.com con el número listo para marcar' : 'Opens voice.google.com with the number ready to dial'}>
          <i className="fa-brands fa-google" aria-hidden="true"></i> {es ? 'Llamar con Google Voice' : 'Call with Google Voice'}
        </a>
      )}
      {address
        ? <a className="claim-action" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`} target="_blank" rel="noopener noreferrer"><i className="fa-solid fa-map-location-dot" aria-hidden="true"></i> {es ? 'Abrir en el mapa' : 'Open in maps'}</a>
        : missing(es ? 'Sin dirección' : 'No address')}
      {record.clientEmail
        ? <a className="claim-action" href={`mailto:${record.clientEmail}`}><i className="fa-solid fa-envelope" aria-hidden="true"></i> {es ? 'Mandar correo' : 'Send email'}</a>
        : missing(es ? 'Sin correo' : 'No email')}
    </div>
  );
};
